import { useEffect, useState } from 'react';

import { sizeByKey } from '~/shared/utils/sizeConstants';
import { parseBraSizes } from '~/techstyle-shared/react-components/src/utils/sizes';
import { useDomain } from '~/techstyle-shared/redux-core';

import { getInitialSelectedBandSizeValue } from '../utils/filters';

import useUnifiedProductGridData from './useUnifiedProductGridData';

const useBraSizeFilter = ({ options, facetName }) => {
  const { selectedUrlFilters } = useUnifiedProductGridData();
  const domain = useDomain();

  const braSizes = parseBraSizes({
    braSizes: options.map((option) => option.value),
  });

  const availableBandSizes = Object.keys(braSizes).map((bandSize) => ({
    label: sizeByKey({
      domain,
      gender: 'F',
      category: 'band-size',
      key: bandSize,
    }),
    value: bandSize,
  }));

  const [selectedBandSizeValue, setSelectedBandSizeValue] = useState(() =>
    getInitialSelectedBandSizeValue({
      selectedUrlFilters,
      availableBandSizes,
      facetName,
    })
  );

  const availableCupSizes = braSizes?.[selectedBandSizeValue]?.map(
    (cupSize) => ({
      label: sizeByKey({
        domain,
        gender: 'F',
        category: 'cup-size',
        key: cupSize,
      }),
      value: cupSize,
    })
  );

  // Update selectedBandSizeValue if the current value does not exist in availableBandSizes
  // due to dynamic filter options being returned from UI interactions.
  // This only effectively applies for Desktop since the filters are applied immediately on selection.
  // Mobile filters are applied on submit button, so we won't have an issue of selecting non-existing band sizes.
  useEffect(() => {
    const currentSelectedBandSizeExists = availableBandSizes.some(
      (bandSize) => bandSize.value === selectedBandSizeValue
    );

    if (currentSelectedBandSizeExists) {
      return;
    }

    const newSelectedBandSize = getInitialSelectedBandSizeValue({
      selectedUrlFilters,
      availableBandSizes,
      facetName,
    });
    setSelectedBandSizeValue(newSelectedBandSize);
  }, [
    availableBandSizes,
    facetName,
    selectedBandSizeValue,
    selectedUrlFilters,
  ]);

  return {
    availableBandSizes,
    availableCupSizes,
    selectedBandSizeValue,
    setSelectedBandSizeValue,
  };
};

export default useBraSizeFilter;
